import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { AU, CA, GB, US } from '../../../../../../../constants/country-codes';
import { getCarriersPath } from '../../../../../../../routes/constants';
import { Segment } from '../../../../../../../services/analytics-services/segment';
import Global from '../../../../../../../store/global-selectors';
import { ClickEvents, EventSourceStrings } from '../../../../../../../tracking/tracking-constants';
import { WalletRegistrationOrUpdateButton } from '../../../../../../../wallet/components/wallet-registration-or-update-button';
import { PAYMENT_TAB } from '../../../../../../../wallet/utilities/constants';
import AULogoCarrier from '../../../../svgs/au-logo+carrier.svg';
import canadaLogoCarrier from '../../../../svgs/canada-carrier-logo.svg';
import GBLogoCarrier from '../../../../svgs/gb-logo+carrier.svg';
import logoCarrier from '../../../../svgs/logo+carrier.svg';
import messages from '../messages';
import style from '../style.module.less';

const configByCountry = {
  [CA]: { src: canadaLogoCarrier, message: messages.savingUpPercentageMessage },
  [US]: { src: logoCarrier, message: messages.savingUpPercentageMessage },
  [GB]: { src: GBLogoCarrier, message: messages.savingUpMessage },
  [AU]: { src: AULogoCarrier, message: messages.savingUpMessage },
};

/**
 * Unregistered view component for the carrier step
 * @returns {JSX.Element}
 */
export const UnregisteredView = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const config = configByCountry[sellerHomeCountry] || configByCountry[US];

  const redirectToCarrierSettings = useCallback(() => {
    Segment.trackEvent(ClickEvents.FTU_CARRIER_STEP_ACTIVATE_CARRIERS_CLICKED, {
      source: EventSourceStrings.FTU_WIZARD_CARRIER_STEP,
    });
    navigate(getCarriersPath());
  }, [navigate]);

  return (
    <FlexColumn className={style['carriers-container']}>
      <FlexColumn className={style['inner-background-section']}>
        <FlexRow className={style['carriers-logos']}>
          <img alt={intl.formatMessage(messages.logoAlt)} src={config.src} />
        </FlexRow>
        <Text className={style['saving-message']} textType={TextType.H3} value={config.message} />
      </FlexColumn>
      <FlexRow className={style['setup-buttons']}>
        <FlexRow>
          <WalletRegistrationOrUpdateButton initialStepId={PAYMENT_TAB} trackingId={EventSourceStrings.FTU_WIZARD_CARRIER_STEP}>
            {({ openModal }) => (
              <Button className={style['setup-button']} onClick={openModal} theme={THEME_CONSTANTS.primary}>
                <Text textType={TextType.Paragraph} value={messages.setupPayment} />
              </Button>
            )}
          </WalletRegistrationOrUpdateButton>
        </FlexRow>
        <FlexRow className={style['edit-button']}>
          <Button onClick={redirectToCarrierSettings} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={messages.activateCarriersLinkText} />
          </Button>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
};
